/** Top-of-page summary banner for the audit detail page. Distilled from
 * the verify response: counts, chain head, anchored vs pending tokens. */
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@/lib/format";

import type { VerifyResponse } from "./types";


type Props = {
  verify: VerifyResponse;
};


export function StatusBlock({ verify }: Props) {
  const { t } = useTranslation();
  const anchored = verify.token_results.filter((t) => t.state === "anchored");
  const pending = verify.token_results.filter((t) => t.state === "pending");
  const failed = verify.token_results.filter((t) => t.state === "failed");

  let badge: { key: string; className: string };
  if (verify.broken) {
    badge = {
      key: "ui.statusBlock.tampered",
      className: "bg-cvp-danger/20 text-cvp-danger ring-cvp-danger/40",
    };
  } else if (verify.ok && anchored.length > 0) {
    badge = {
      key: "ui.statusBlock.verified",
      className: "bg-cvp-ok/20 text-cvp-ok ring-cvp-ok/40",
    };
  } else if (pending.length > 0) {
    badge = {
      key: "ui.statusBlock.anchoring",
      className: "bg-cvp-warn/20 text-cvp-warn ring-cvp-warn/40",
    };
  } else {
    badge = {
      key: "ui.statusBlock.noAnchors",
      className: "bg-slate-700/40 text-nb-text ring-slate-700/60",
    };
  }

  return (
    <section className="card space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ring-1 ${badge.className}`}
          >
            {t(badge.key)}
          </span>
          <span className="text-sm text-nb-text-2">
            {t("ui.statusBlock.counts", {
              count: verify.events,
              anchored: anchored.length,
              pending: pending.length,
            })}
            {failed.length > 0 &&
              t("ui.statusBlock.failedSuffix", { failed: failed.length })}
          </span>
        </div>
        {verify.audit_chain_started_at && (
          <span className="text-xs text-nb-text-3">
            {t("ui.statusBlock.chainOpened", {
              when: formatDateTime(verify.audit_chain_started_at),
            })}
          </span>
        )}
      </div>
      {verify.chain_head_hash && (
        <div className="flex items-baseline gap-2 text-xs">
          <span className="text-nb-text-3">{t("ui.statusBlock.head")}</span>
          <code className="font-mono text-nb-text">{verify.chain_head_hash}</code>
        </div>
      )}
    </section>
  );
}
